import { buttonClasses } from '@mui/material';
import type { Components, Theme } from '@mui/material/styles';

import palette from '@/theme/palette.ts';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'text-icon': true;
  }
}

const MuiButton: Components<Theme>['MuiButton'] = {
  styleOverrides: {
    root: ({ theme: { spacing } }: { theme: Theme }) => ({
      paddingInlineEnd: spacing(1.5),
      paddingInlineStart: spacing(1.5),
      paddingTop: spacing(0.375),
      paddingBottom: spacing(0.375),
      borderWidth: spacing(0.125),
      borderStyle: 'solid',
      borderColor: palette.primary.main,
      textTransform: 'capitalize',
      boxShadow: 'none',
      lineHeight: spacing(3),
      minWidth: 'auto',
      alignSelf: 'center',

      '& .MuiButton-startIcon': {
        marginInlineStart: 0,
        marginInlineEnd: spacing(0.5)
      },
      '& .MuiButton-endIcon': {
        marginInlineStart: 0,
        marginInlineEnd: spacing(5)
      },
      '&.Mui-disabled': {
        backgroundColor: palette.grey['300'],
        borderColor: palette.grey['300'],
        color: palette.grey['800']
      }
    }),
    contained: {
      backgroundColor: palette.primary.main,
      '&:hover': {
        backgroundColor: palette.primary[900],
        boxShadow: 'none'
      },
      '&:active': {
        backgroundColor: palette.primary[1000],
        boxShadow: 'none'
      }
    }
  },
  variants: [
    {
      props: ({ startIcon }) => !!startIcon,
      style: ({ theme: { spacing } }) => ({
        paddingLeft: spacing(1),

        [`& .${buttonClasses.startIcon}`]: {
          marginInlineEnd: spacing(1)
        },

        [`& .${buttonClasses.startIcon} svg`]: {
          width: spacing(2.5),
          height: spacing(2.5)
        }
      })
    },
    {
      props: ({ variant }) => variant === 'text' || variant === 'text-icon',
      style: ({ theme: { palette } }) => ({
        borderColor: 'transparent',

        '&:hover': {
          backgroundColor: palette.grey[100]
        },
        '&:active': {
          backgroundColor: palette.primary[50]
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        }
      })
    },
    {
      props: { variant: 'text-icon' },
      style: ({ theme: { spacing } }) => ({
        padding: spacing(0.625, 0.875),
        minWidth: 'auto',

        [`& .${buttonClasses.startIcon}`]: {
          margin: 0
        }
      })
    }
  ]
};

export default MuiButton;
